import { authenticationClient, client } from './Client';

export const submissionsApi = {
  // RMS Direct Login - No OTP needed
  login: (credentials) =>
    authenticationClient.post('/auth/login', credentials),

  getSubmissionOverallEffectiveness: (submissionId) =>
    client.get(`/api/v1/submissions/${submissionId}/overall-effectiveness`),

  downloadFramework: (frameworkId) =>
    client.get(`/api/v1/frameworks/${frameworkId}/download`),

  downloadUploadedFile: async (fileId) => {
    // Check if fileId is an S3 key (RMS upload) - convert to string first
    if (fileId && String(fileId).includes('/')) {
      return submissionsApi.downloadRMSEvidence(fileId);
    }
    return client.get(`/api/v1/indicator-submissions/files/${fileId}/download`);
  },

  getAll: () => client.get('/api/v1/submissions'),

  createSubmission: (data) => client.post('/api/v1/submissions', data),

  getIndicators: (submissionId) =>
    client.get(`/api/v1/submissions/${submissionId}/indicators`),

  getStandardsIndicators: (submissionId) =>
    client.get(`/api/v1/submissions/${submissionId}/sections-with-indicators`),

  getAllFrameworks: () =>
    client.get('/api/v1/frameworks'),

  getIndicatorSubmission: (id) =>
    client.get(`/api/v1/indicator-submissions/${id}`),

  updateIndicatorjudgment: (indicatorSubmissionId, judgment) =>
    client.put(`/api/v1/indicator-submissions/${indicatorSubmissionId}/judgment`, { id: indicatorSubmissionId, judgment }),

  updateIndicatorText: (id, text) =>
    client.put(`/api/v1/indicator-submissions/${id}/text`, { text }),

  getAgentRecommendation: (id) =>
    client.get(`/api/v1/agent-recommendations/${id}`),

  getAgentSubmissionRecommendation: (id) =>
    client.get(`/api/v1/agent-submission-recommendations/${id}`),

  invokeAiCheck: (id) =>
    client.post(`/api/v1/indicator-submissions/${id}/invoke-ai-check`),



  // New evidence-based upload method
  generateEvidenceUploadUrl: (indicatorSubmissionId, evidenceId, submissionData) => {
    return client.post(`/api/v1/indicator-submissions/${indicatorSubmissionId}/evidences/${evidenceId}/files/upload-url`, submissionData)
  },

  markUploadComplete: (fileId) =>
    client.post(`/api/v1/indicator-submissions/files/${fileId}/complete`),

  deleteUploadedFile: (fileId) =>
    client.delete(`/api/v1/indicator-submissions/files/${fileId}`),

  getLatestAgentSubmissionRecommendations: (submissionId) =>
    client.get(`/api/v1/agent-latest-submission-recommendations/${submissionId}`),

  // RMS Authentication - Direct login without OTP
  authenticate: async (credentials) => {
    try {
      const response = await authenticationClient.post('/auth/login', credentials);
      return response;
    } catch (error) {
      if (error.response?.status === 408) {
        throw new Error('Server is currently unavailable. Please try again later.');
      } else if (error.response?.status === 401) {
        throw new Error('Invalid credentials. Please check your email and password.');
      } else if (!error.response?.data?.success) {
        throw new Error(error.response?.data?.message || 'An unexpected error occurred. Please try again.');
      } else {
        throw new Error('An unexpected error occurred. Please try again.');
      }
    }
  },

  // Token verification for RMS
  verifyToken: async (token) => {
    try {
      const response = await authenticationClient.post('/auth/verify-token', { token });
      return response;
    } catch (error) {
      if (!error.response?.data?.success) {
        throw new Error(error.response?.data?.message || 'Token verification failed.');
      }
      throw new Error('Token verification failed.');
    }
  },

  submitSubmission: async (submissionId) => {
    try {
      const response = await client.post(`/api/v1/submissions/${submissionId}/submit`);
      return response;
    } catch (error) {
      if (error.response?.data?.detail) {
        const { error_code, message } = error.response.data.detail;
        switch (error_code) {
          case 1:
            throw new Error('Some indicator submissions are missing file uploads. Please upload all required files before submitting.');
          case 2:
            throw new Error('Some indicator submissions are missing text input. Please provide all required text inputs before submitting.');
          case 3:
            throw new Error('Some indicator submissions have not been processed by AI. Please wait for AI processing to complete before submitting.');
          case 9:
            throw new Error('Overall Effectiveness Self-Evaluation incomplete. Please select a judgment and provide text.');
          default:
            throw new Error(message || 'An error occurred while submitting the application.');
        }
      } else if (!error.response?.data?.success) {
        throw new Error(error.response?.data?.message || 'An unexpected error occurred while submitting the application. Please try again.');
      }
      throw new Error('An unexpected error occurred while submitting the application. Please try again.');
    }
  },

  // Institution Profile Methods
  createInstitutionProfile: async (formData) => {
    try {
      const response = await client.post('/api/v1/institution/profile', formData);
      return response;
    } catch (error) {
      if (error.response?.status === 400) {
        if (error.response?.data?.detail) {
          throw new Error(error.response.data.detail);
        } else if (!error.response?.data?.success) {
          throw new Error(error.response?.data?.message || 'Please check your profile data and try again.');
        }
        throw new Error('Please check your profile data and try again.');
      } else if (error.response?.status === 409) {
        throw new Error('A profile already exists for this institution.');
      }
      throw new Error('Failed to create profile. Please try again.');
    }
  },

  getInstitutionProfile: async () => {
    try {
      const response = await client.get('/api/v1/institution/profile');
      return response;
    } catch (error) {
      if (error.response?.status === 404) {
        return null; // No profile exists yet
      }
      throw new Error('Failed to load profile.');
    }
  },

  updateInstitutionProfile: async (formData) => {
    try {
      const response = await client.put('/api/v1/institution/profile', formData);
      return response;
    } catch (error) {
      if (error.response?.status === 400) {
        if (error.response?.data?.detail) {
          throw new Error(error.response.data.detail);
        } else if (!error.response?.data?.success) {
          throw new Error(error.response?.data?.message || 'Please check your profile data and try again.');
        }
        throw new Error('Please check your profile data and try again.');
      } else if (error.response?.status === 404) {
        throw new Error('No profile found to update.');
      }
      throw new Error('Failed to update profile. Please try again.');
    }
  },

  updateSubmissionOverallEffectivenessText: (submissionId, overall_effectiveness_text) =>
    client.put(`/api/v1/submissions/${submissionId}/overall-effectiveness-text`, { overall_effectiveness_text }),

  updateSubmissionOverallEffectivenessJudgment: (submissionId, overall_effectiveness_judgment) =>
    client.put(`/api/v1/submissions/${submissionId}/overall-effectiveness-judgment`, { overall_effectiveness_judgment }),

  // RMS Sync during final submission - sync all files to RMS
  syncFilesToRMS: async (submissionId) => {
    const response = await client.post(`/api/v1/submissions/${submissionId}/sync-to-rms`);
    return response;
  },

  // NEW: Sync to RMS with background task
  syncToRMS: async (submissionId) => {
    const response = await client.post(`/api/v1/submissions/${submissionId}/sync-to-rms`);
    return response;
  },

  // NEW: Get RMS sync status
  getRMSSyncStatus: async (submissionId) => {
    const response = await client.get(`/api/v1/submissions/${submissionId}/rms-sync-status`);
    return response;
  },

  saveRMSMetadata: async (files, ev_no, review, title, indicator_submission_id, evidence_id) => {
    const response = await client.post('/api/v1/upload-evidence/save-metadata', {
      files,
      ev_no,
      review,
      title,
      indicator_submission_id,
      evidence_id
    });
    return { data: response };
  },

  uploadToRMSS3: async (file, presignedUrl) => {
    // Upload through backend to avoid CORS issues
    const formData = new FormData();
    formData.append('file', file);
    formData.append('presignedUrl', presignedUrl);

    const token = localStorage.getItem('access_token');
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/upload-evidence/upload-to-s3`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
        // Don't set Content-Type - let browser set it for FormData
      },
      body: formData
    });

    if (!response.ok) {
      const error = new Error('Upload failed');
      error.response = { status: response.status, data: await response.json() };
      throw error;
    }

    return response.json();
  },

  downloadRMSEvidence: async (documentName) => {
    const response = await client.post('/api/v1/download', {
      documentName: documentName
    });

    // RMS returns JSON with downloadUrl, not file content
    if (response.data && response.data.downloadUrl) {
      // Return the S3 download URL directly
      return { url: response.data.downloadUrl };
    }

    throw new Error('No download URL received from RMS');
  },

  // Polling endpoint for background sync status
  getSubmissionSyncStatus: (submissionId) =>
    client.get(`/api/v1/submissions/${submissionId}/sync-status`),
};